import { isAxiosError } from 'axios';
import { BidComparisonColumnStatus } from '@/features/bid-comparison/constants';
import type {
  BidComparison,
  BidComparisonBid,
  BidComparisonColumnItem,
  BidComparisonScope,
} from '@/features/bid-comparison/types';
import { SUBCONTRACTOR_OFFER_TYPE } from '@/features/subcontractor-bids/constants';

export const isComparisonSheetMissing = (error: unknown) =>
  isAxiosError(error) &&
  error.response?.status === 404 &&
  error.response?.data?.detail?.code === 'comparison_sheet_not_found';

export const bidComparisonScope = ({
  procurementId,
}: {
  procurementId?: string;
}): BidComparisonScope =>
  procurementId ? { offerType: SUBCONTRACTOR_OFFER_TYPE, workPackageId: procurementId } : undefined;

export const isAnalysisColumn = (
  columnType?: string,
): columnType is 'bid_methodology' | 'cv_analysis' =>
  columnType === 'bid_methodology' || columnType === 'cv_analysis';

export const findBidColumn = (
  bid: BidComparisonBid,
  column: Pick<BidComparisonColumnItem, 'id' | 'columnType'>,
) =>
  isAnalysisColumn(column.columnType)
    ? bid.columns.find((cell) => cell.columnType === column.columnType)
    : bid.columns.find((cell) => cell.userQuery === column.id);

export const isComparisonProcessing = (bidComparison: BidComparison | undefined) =>
  !!bidComparison?.bids.some((bid) =>
    bid.columns.some(
      (column) =>
        column.status === BidComparisonColumnStatus.IN_PROGRESS ||
        column.analysisStatus === BidComparisonColumnStatus.IN_PROGRESS,
    ),
  ) ||
  !!bidComparison?.aiRowSummaries?.some(
    (summary) => summary.status === BidComparisonColumnStatus.IN_PROGRESS,
  );

export const getAiSummaryState = (bidComparison: BidComparison | undefined, userQuery: string) => {
  const column = bidComparison?.bids
    .flatMap((bid) => bid.columns)
    .find((cell) => cell.userQuery === userQuery);
  const columnId = column?.columnId;
  const isAnalysis = isAnalysisColumn(column?.columnType);
  const cells = (bidComparison?.bids ?? []).map((bid) => {
    const bidColumn = findBidColumn(bid, { id: userQuery, columnType: column?.columnType });
    return {
      companyName: bid.companyName,
      status: isAnalysis ? bidColumn?.analysisStatus : bidColumn?.status,
      isStale: bidColumn?.isStale ?? false,
    };
  });
  const summary = bidComparison?.aiRowSummaries?.find((item) => item.userQuery === userQuery);
  const incompleteBidders = cells.filter(
    (cell) => cell.status !== BidComparisonColumnStatus.DONE || cell.isStale,
  );
  const isProcessing =
    summary?.status === BidComparisonColumnStatus.IN_PROGRESS ||
    cells.some((cell) => cell.status === BidComparisonColumnStatus.IN_PROGRESS);

  return {
    columnId,
    summary,
    incompleteBidders,
    isProcessing,
    canRun: !!columnId && !isProcessing && incompleteBidders.length === 0,
  };
};
